/**
 * Language-code handling for the widget.
 *
 * Three forms of the same locale are in play, and conflating them is the class of bug this module
 * exists to prevent:
 *
 * | form         | looks like          | used for                                                    |
 * | ------------ | ------------------- | ----------------------------------------------------------- |
 * | wire         | `pt-br`, `zh-hans`  | the WS `configure` payload — matches `Casino.languages`      |
 * | resource     | `pt-br`, `zh`       | the bundled locale JSON filenames in `assets/locales`        |
 * | field suffix | `pt_br`, `zh_hans`  | backend modeltranslation keys (`content_pt_br`)              |
 *
 * Previously every code was truncated to two characters (`pt-BR` -> `pt`), which collapsed the
 * distinctions above and made `pt-BR`/`zh-Hans` unreachable — see #3785.
 */

export const DEFAULT_LANGUAGE = "en";

/** `en`, or a regional variant such as `pt-br` / `zh-hans`. */
const LANGUAGE_CODE = /^[a-z]{2}(-[a-z]{2,4})?$/;

/**
 * Locale-file aliases: Simplified Chinese lives in `zh.json` and Bokmål in `no.json` (the names
 * the overlay uses too), so neither wire code can index the
 * bundled `resources` directly. Region-only variants (`fr-ca`, `en-nz`) need no entry — stripping
 * the region already yields a real file.
 */
const RESOURCE_ALIASES: Record<string, string> = {
  "zh-hans": "zh",
  nb: "no",
};

/**
 * Host-supplied code -> canonical wire form: trimmed, lowercased, `_` -> `-`. `pt_BR`, ` PT-br `
 * and `pt-BR` all collapse to `pt-br`. Anything not shaped like a language code (`xyzzy`, `eng`)
 * falls back to {@link DEFAULT_LANGUAGE} rather than being sent on and rejected by the backend.
 */
export function normalizeLanguage(raw: string | null | undefined): string {
  const code = (raw ?? "").trim().toLowerCase().replace(/_/g, "-");
  return LANGUAGE_CODE.test(code) ? code : DEFAULT_LANGUAGE;
}

/**
 * Wire code -> the bundled locale to render from: the exact file when present, else the base
 * language, else English. `available` is the set of bundled locales (`Object.keys(resources)`),
 * passed in so this stays independent of i18next's init order.
 */
export function resolveResourceLanguage(
  language: string | null | undefined,
  available: readonly string[]
): string {
  const code = normalizeLanguage(language);
  const aliased = RESOURCE_ALIASES[code] ?? code;

  return (
    [aliased, aliased.split("-")[0], DEFAULT_LANGUAGE].find((candidate) =>
      available.includes(candidate)
    ) ?? DEFAULT_LANGUAGE
  );
}

/**
 * Candidate modeltranslation field suffixes, most specific first: `pt-BR` -> `["pt_br", "pt",
 * "en"]`. Mirrors the backend's `common.language.language_fallback_chain`, which is what decides
 * these field names in the first place.
 */
export function languageFieldSuffixes(
  language: string | null | undefined
): string[] {
  const code = normalizeLanguage(language);
  const suffixes = [code.replace(/-/g, "_")];

  for (const candidate of [code.split("-")[0], DEFAULT_LANGUAGE]) {
    if (!suffixes.includes(candidate)) suffixes.push(candidate);
  }

  return suffixes;
}
